import axios from 'axios'
import { apiConfig, ApiError, isProduction } from '../types/api'
import type {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import type { ApiResponse } from '../types/api'
import { refreshTokenWithLock, addToken, relogin } from './tokenUtils'
import { Toast } from 'antd-mobile'
import {
  logOnRequestError,
  logOnRequestSuccess,
  logOnResponseError,
  logOnResponseSuccess,
} from './log'
import plugin from './plugin'
import { isShadowWorked, setShadowWorked, unSetShadowWorked } from './shadowMode'

// 扩展 axios 配置类型，支持 shadow 选项
declare module 'axios' {
  export interface AxiosRequestConfig {
    shadow?: boolean
  }
}

function addShadow(config: InternalAxiosRequestConfig) {
  if (!isShadowWorked()||isProduction()||!config.shadow) return config
  config.headers['X-Shadow-System'] = 'test-shadow-token'
  return config
}
const requestStack: InternalAxiosRequestConfig[] = []
const httpsSuccessResStack: AxiosResponse<ApiResponse>[] = []
const httpsErrorResStack: AxiosError[] = []
export function getResStack() {
  return [httpsSuccessResStack, httpsErrorResStack] as const
}
export function getReqStack() {
  return requestStack
}
export function clearStacks() {
  requestStack.length = 0
  httpsSuccessResStack.length = 0
  httpsErrorResStack.length = 0
}
/**
 * HTTP请求工具模块
 * 基于axios封装，处理请求/响应拦截、Token自动刷新、错误统一处理等
 */

// 创建 axios 实例
const apiClient = axios.create(apiConfig)

/**
 * 请求拦截器
 * 功能：
 * 1. Token有效性检查与自动刷新
 * 2. 请求头Authorization自动添加
 * 3. 并发请求Token冲突处理
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) =>
    await plugin(config, [
      addToken,
      logOnRequestSuccess,
      addShadow,
      (config) => {
        requestStack.push(config)
        return config
      },
    ]),

  (error: AxiosError) => {
    logOnRequestError(error)
    throw error
  },
)
/**
 * 响应拦截器
 * 功能：
 * 1. 响应数据格式化（提取业务数据）
 * 2. 业务错误统一处理（基于code码）
 * 3. HTTP错误分类处理（网络错误、服务器错误等）
 */
apiClient.interceptors.response.use(
  async (response: AxiosResponse<ApiResponse>) => {
    const { data } = response
    logOnResponseSuccess(response)
    httpsSuccessResStack.push(response)
    // 1. 业务成功（code=200）：返回业务数据部分
    if (data.code.toString() === '200') {
      return data.data
    }
    if (data.code.toString() === '40100' || data.code.toString() === '40300') {
      // 触发刷新token
      if (data.code.toString() === '40300') {
        relogin('请重新登录')
      }
      refreshTokenWithLock().catch(() => {
        relogin('请重新登录')
      })
    }
    // 2. 业务失败：抛出自定义ApiError（包含业务code和msg）
    throw new ApiError(data.code, data.msg)
  },
  (error: AxiosError) => {
    httpsErrorResStack.push(error)
    logOnResponseError(error)

    // 3. 错误分类处理
    if (error.response) {
      // 3.1 服务器返回错误状态码（非2xx）
      const status = error.response.status

      const message = `请求失败 (${status})`

      throw new ApiError(status.toString(), message + JSON.stringify(error.toJSON()))
    } else if (error.request) {
      // 3.2 网络错误（请求发出但无响应）
      Toast.show({
        content: '网络连接失败，请检查网络',
      })
      throw new ApiError('NETWORK_ERROR', '网络连接失败，请检查网络' + JSON.stringify(error.toJSON()))
    } else {
      // 3.3 其他错误（如请求配置错误）
      Toast.show({
        content: error.message || '未知错误',
      })
      throw new ApiError('UNKNOWN_ERROR', (error.message || '未知错误') + JSON.stringify(error.toJSON()))
    }
  },
)

export default apiClient
export { isShadowWorked, setShadowWorked, unSetShadowWorked }
